import { useState, useEffect, useRef } from 'react';
import { Card } from '../../../../components/ui/Card';

export function KpiLine({ allWorkOrders, chartJsLoaded, selectedLine }) {
    const lineCanvasRef = useRef(null);
    const lineChartRef = useRef(null);
    const [lineChartView, setLineChartView] = useState('detailed'); // 'aggregate' or 'detailed'

    useEffect(() => {
        if (!chartJsLoaded || !lineCanvasRef.current || !allWorkOrders) return;

        if (lineChartRef.current) {
            lineChartRef.current.destroy();
        }

        const ctx = lineCanvasRef.current.getContext('2d');

        const months = [];
        const now = new Date();
        for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.push({
                label: d.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }),
                key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
            });
        }

        const counts = {
            corrective: {},
            predictive: {},
            preventive: {}
        };
        months.forEach(m => {
            counts.corrective[m.key] = 0;
            counts.predictive[m.key] = 0;
            counts.preventive[m.key] = 0;
        });
        if (selectedLine !== null) {
            const lineWorkOrders = [
                ...(selectedLine?.lineWorkOrders?.correctiveWorkOrders || []),
                ...(selectedLine?.lineWorkOrders?.predictiveWorkOrders || []),
                ...(selectedLine?.lineWorkOrders?.preventiveWorkOrders || [])
            ];


            lineWorkOrders.forEach(wo => {
                const status = wo.status?.toLowerCase();
                const dateStr = (status === 'scheduled' || status === 'pending')
                    ? wo.scheduled_date
                    : wo.start_date;

                if (dateStr) {
                    const d = new Date(dateStr);
                    if (!isNaN(d.getTime())) {
                        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        const type = wo.work_order_type?.toLowerCase();
                        if (counts[type] && counts[type][key] !== undefined) {
                            counts[type][key]++;
                        }
                    }
                }
            });

        } else {


            allWorkOrders.forEach(wo => {
                const status = wo.status?.toLowerCase();
                const dateStr = (status === 'scheduled' || status === 'pending')
                    ? wo.scheduled_date
                    : wo.start_date;

                if (dateStr) {
                    const d = new Date(dateStr);
                    if (!isNaN(d.getTime())) {
                        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        const type = wo.work_order_type?.toLowerCase();
                        if (counts[type] && counts[type][key] !== undefined) {
                            counts[type][key]++;
                        }
                    }
                }
            });
        }

        const labels = months.map(m => m.label);
        let datasets = [];
        let legendDisplay = true;

        const aggregateData = months.map(m => {
            return counts.corrective[m.key] + counts.predictive[m.key] + counts.preventive[m.key];
        });
        const maxTotal = Math.max(...aggregateData, 5);

        if (lineChartView === 'aggregate') {
            legendDisplay = false;
            const gradient = ctx.createLinearGradient(0, 0, 0, 250);
            gradient.addColorStop(0, 'rgba(12, 219, 23, 0.35)');
            gradient.addColorStop(1, 'rgba(62, 190, 100, 0)');

            datasets = [{
                label: 'Total Órdenes de Trabajo',
                data: aggregateData,
                fill: true,
                backgroundColor: gradient,
                borderColor: 'rgba(42, 212, 0, 1)',
                borderWidth: 2,
                tension: 0,
                pointBackgroundColor: 'rgba(42, 212, 0, 1)',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgba(42, 212, 0, 1)',
                pointRadius: 4,
                pointHoverRadius: 6
            }];
        } else {
            legendDisplay = true;
            const correctiveData = months.map(m => counts.corrective[m.key]);
            const predictiveData = months.map(m => counts.predictive[m.key]);
            const preventiveData = months.map(m => counts.preventive[m.key]);

            datasets = [
                {
                    label: 'Correctivas (Reactiva)',
                    data: correctiveData,
                    fill: false,
                    borderColor: 'rgba(239, 68, 68, 1)',
                    borderWidth: 2,
                    tension: 0,
                    pointBackgroundColor: 'rgba(239, 68, 68, 1)',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgba(239, 68, 68, 1)',
                    pointRadius: 4,
                    pointHoverRadius: 6
                },
                {
                    label: 'Predictivas',
                    data: predictiveData,
                    fill: false,
                    borderColor: 'rgba(59, 130, 246, 1)',
                    borderWidth: 2,
                    tension: 0,
                    pointBackgroundColor: 'rgba(59, 130, 246, 1)',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgba(59, 130, 246, 1)',
                    pointRadius: 4,
                    pointHoverRadius: 6
                },
                {
                    label: 'Preventivas',
                    data: preventiveData,
                    fill: false,
                    borderColor: 'rgba(132, 204, 22, 1)',
                    borderWidth: 2,
                    tension: 0,
                    pointBackgroundColor: 'rgba(132, 204, 22, 1)',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgba(132, 204, 22, 1)',
                    pointRadius: 4,
                    pointHoverRadius: 6
                }
            ];
        }

        lineChartRef.current = new window.Chart(ctx, {
            type: 'line',
            data: {
                labels: labels,
                datasets: datasets
            },
            options: {
                animation: false,
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMax: maxTotal,
                        grid: {
                            color: 'rgba(255, 255, 255, 0.08)',
                            drawTicks: false
                        },
                        ticks: {
                            color: '#9ca3af',
                            font: { size: 11 },
                            stepSize: 1
                        }
                    },
                    x: {
                        grid: { display: false },
                        ticks: {
                            color: '#9ca3af',
                            font: { size: 11 }
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: legendDisplay,
                        position: 'top',
                        labels: {
                            color: '#e5e7eb',
                            boxWidth: 12,
                            padding: 15,
                            font: {
                                size: 11,
                                weight: 'bold'
                            }
                        }
                    },
                    tooltip: {
                        backgroundColor: '#1f2937',
                        titleColor: '#fff',
                        bodyColor: '#e5e7eb',
                        borderColor: '#374151',
                        borderWidth: 1,
                        padding: 10,
                        cornerRadius: 6,
                        displayColors: true
                    }
                }
            }
        });

        return () => {
            if (lineChartRef.current) {
                lineChartRef.current.destroy();
                lineChartRef.current = null;
            }
        };
    }, [chartJsLoaded, allWorkOrders, lineChartView, selectedLine]);

    return (
        <Card className="p-5 bg-gray-900/40 border border-gray-800">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
                    Histórico de Órdenes de Trabajo ({selectedLine?.line || 'General'} - Últimos 6 Meses)
                </h3>
                <div className="flex items-center bg-gray-800/80 p-0.5 rounded-lg border border-gray-700">
                    <button
                        onClick={() => setLineChartView('aggregate')}
                        className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${lineChartView === 'aggregate'
                            ? 'bg-blue-600 text-white shadow'
                            : 'text-gray-400 hover:text-gray-200'
                            }`}
                    >
                        Total
                    </button>
                    <button
                        onClick={() => setLineChartView('detailed')}
                        className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${lineChartView === 'detailed'
                            ? 'bg-blue-600 text-white shadow'
                            : 'text-gray-400 hover:text-gray-200'
                            }`}
                    >
                        Por Tipo
                    </button>
                </div>
            </div>
            <div className="h-64 relative w-full">
                <canvas ref={lineCanvasRef}></canvas>
            </div>
        </Card>
    );
}
